import type { ReactNode } from 'react';
import { ChevronDown, CircleHelp } from 'lucide-react';
import {
  Alert,
  AlertDescription,
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@design-system';
import { CONSUMER_STEPS, type ConsumerStepId } from '../lib/steps';
import { StepFooter, type StepAction } from './step-footer';

interface StepFrameProps {
  step: ConsumerStepId;
  title: string;
  subtitle?: ReactNode;
  onBack?: () => void;
  backLabel?: string;
  children?: ReactNode;
  help?: ReactNode;
  helpLabel?: string;
  primary?: StepAction;
  secondary?: StepAction;
  next?: string;
  error?: string | null;
}

export function StepFrame({
  step,
  title,
  subtitle,
  onBack,
  backLabel = 'Back',
  children,
  help,
  helpLabel = 'I’m not sure',
  primary,
  secondary,
  next,
  error,
}: StepFrameProps) {
  return (
    <section className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <p className="text-primary text-xs font-semibold uppercase tracking-wide">{CONSUMER_STEPS[step].kicker}</p>
        <h1 className="text-2xl font-semibold text-gray-950 sm:text-3xl">{title}</h1>
        {subtitle && <p className="text-muted-foreground text-base">{subtitle}</p>}
      </div>

      {children}

      {help && (
        <Collapsible className="group/help rounded-lg border bg-white">
          <CollapsibleTrigger asChild>
            <Button variant="ghost" className="w-full justify-between">
              <span className="flex items-center gap-2">
                <CircleHelp />
                {helpLabel}
              </span>
              <ChevronDown className="transition-transform duration-200 ease-out group-data-[state=open]/help:rotate-180" />
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="text-muted-foreground flex flex-col gap-3 px-4 pb-4 pt-3 text-sm">
            {help}
          </CollapsibleContent>
        </Collapsible>
      )}

      {error && (
        <Alert variant="destructive" role="alert">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {next && <p className="text-muted-foreground text-sm">Next: {next}</p>}

      <StepFooter onBack={onBack} backLabel={backLabel} primary={primary} secondary={secondary} />
    </section>
  );
}
